import React, { useEffect, useState } from "react";

/**
 * iOS Auth Bridge — V2 redesign (matches the in-app aesthetic: cool off-white
 * background, Plus Jakarta Sans bold wordmark with "Tip" navy + "Lingo" orange,
 * brand-orange CTA pill, navy body copy).
 *
 * Loaded ONLY by SFSafariViewController after the user completes Google
 * sign-in via Emergent Auth. Receives `#session_id=XXX` and must hand it
 * off to the native app via the registered `tiplingo://` URL scheme.
 *
 * IMPORTANT — SFSafariViewController behavior:
 *   - JS-initiated `window.location.href = "tiplingo://…"` does NOT launch
 *     the app. Worse, it silently dismisses the Safari sheet, stranding the
 *     user with no path back.
 *   - Only a real USER TAP on an `<a href="tiplingo://…">` link launches the
 *     custom URL scheme reliably.
 *
 * Therefore: NO auto-redirect. We render a giant full-screen tap target and
 * the user taps it to finish signing in.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS — THIS BREAKS THE AUTH
 */
export default function IOSAuthBridge() {
  const [sessionId, setSessionId] = useState("");

  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) setSessionId(m[1]);
  }, []);

  // Query string — iOS deep-link delivery to Capacitor is more reliable with
  // query params than with URL fragments. The native app's URL handler parses
  // `?session_id=…` and completes the OAuth exchange. We `encodeURIComponent`
  // the session_id in case it contains characters that need escaping (e.g.
  // base64 padding `=`, `+`, `/`). The native handler decodes it back.
  const target = sessionId
    ? `tiplingo://auth?session_id=${encodeURIComponent(sessionId)}`
    : "";

  return (
    <div
      data-testid="ios-auth-bridge"
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        // V2 cool off-white backdrop with subtle warm + sky radial accents
        // — same palette family as the in-app Home + Travel Vault.
        background:
          "radial-gradient(900px 700px at 15% 12%, #FFE0CC 0%, transparent 60%)," +
          "radial-gradient(900px 700px at 85% 88%, #DCEFE3 0%, transparent 60%)," +
          "#F5F7FA",
        fontFamily:
          "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div className="w-full max-w-sm text-center">
        {/* TipLingo wordmark — exact in-app lockup. Plus Jakarta Sans 800,
            "Tip" in brand navy, "Lingo" in brand orange. */}
        <div
          className="font-extrabold tracking-tight text-5xl mb-3"
          style={{ letterSpacing: "-1.5px" }}
        >
          <span style={{ color: "#082140" }}>Tip</span>
          <span style={{ color: "#E55B2C" }}>Lingo</span>
        </div>

        {/* Subtitle in navy-soft body color */}
        <p
          className="text-base font-medium leading-relaxed mb-10 px-2"
          style={{ color: "#4A5D78" }}
        >
          You're almost in. Tap below to return to the TipLingo app and finish
          signing in.
        </p>

        {target ? (
          <a
            href={target}
            data-testid="ios-auth-bridge-open"
            className="block w-full rounded-2xl py-5 px-6 font-extrabold text-base uppercase tracking-widest transition-transform active:scale-[0.98]"
            style={{
              background: "#E55B2C",
              color: "#FFFFFF",
              boxShadow: "0 14px 36px -10px rgba(229, 91, 44, 0.55)",
              letterSpacing: "0.14em",
            }}
          >
            Open TipLingo →
          </a>
        ) : (
          <div
            className="text-sm font-medium"
            style={{ color: "#4A5D78" }}
            data-testid="ios-auth-bridge-no-session"
          >
            No session detected — please restart sign-in from the app.
          </div>
        )}

        {/* Footer flourish — small wordmark dot accent matching the App Store
            posters, anchors the bottom of the card with brand color. */}
        <div className="mt-12 flex items-center justify-center gap-3 opacity-70">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "#E55B2C" }}
          />
          <span
            className="text-xs font-bold uppercase"
            style={{
              color: "#082140",
              letterSpacing: "0.42em",
            }}
          >
            TipLingo
          </span>
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "#E55B2C" }}
          />
        </div>
      </div>
    </div>
  );
}
